/*----------------------------------
- ROUTE: /product/appraise
----------------------------------*/

/**
 * @docs docs/features/website-product-feature-pages
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import { getProductFeaturePage, productFeaturePageMirrorPath, productFeaturePagePath } from '@/common/catalogs/landing/productPages';
import { PRODUCT_APPRAISE_PAGE_CONTENT } from '@/client/catalogs/landing/product/appraise/pageContent';
import { buildProductAppraiseGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { buildProductFeatureStructuredData, toStructuredDataFaqs, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import ProductAppraisePage from './page/ProductAppraisePage';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';

/*----------------------------------
- ROUTES
----------------------------------*/

export default definePageRoute({
    path: '/product/appraise',
    options: {
        auth: false,
        layout: false,
        static: {
            refresh: '1 day',
            urls: ['/product/appraise'],
        },
    },
    data: ({ Product }) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    }),
    render: ({
        page,
        Router,
        navigation,
    }: {
        page: {
            description?: string;
            head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
            jsonld: WebsiteJsonLdNode[];
            title?: string;
            url: string;
        };
        Router: { url: (path: string) => string };
        navigation: LandingHeaderDomainsNavigation;
    }) => {
        const entry = getProductFeaturePage('appraise');
        const canonicalUrl = Router.url(productFeaturePagePath('appraise'));

        if (entry) {
            page.title = entry.pageTitle;
            page.description = entry.metaDescription;
        }
        page.url = canonicalUrl;
        page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
        page.head.push({
            $: 'link',
            rel: 'alternate',
            type: 'text/markdown',
            href: Router.url(productFeaturePageMirrorPath('appraise')),
        });
        // Every FAQ emitted here is rendered in the page accordion. Marking up an
        // answer the visitor cannot see is a structured-data violation, so the two
        // lists are deliberately the same array.
        page.jsonld.push(
            ...buildProductFeatureStructuredData({
                canonicalUrl,
                description: entry?.metaDescription || '',
                faqs: toStructuredDataFaqs(PRODUCT_APPRAISE_PAGE_CONTENT.faq.items),
                featureLabel: entry?.label || 'Appraise',
                geoContext: buildProductAppraiseGeoContext(),
                title: entry?.pageTitle || 'Appraise',
            }),
        );

        return <ProductAppraisePage navigation={navigation} />;
    },
});
